import { ChangeEvent, useCallback, useEffect } from 'react';
import useInvoicePoColumns from 'src/assets/static/enter_invoice/useInvoicePoColumns';
import Alert from 'src/components/Alert';
import SearchInput from 'src/components/SearchInput';
import useAttachDocument from 'src/hooks/useAttachDocument';
import useEnterInvoices from 'src/hooks/useEnterInvoices';
import {
  ChooseInvoice,
  EnterInvoice,
  EnterPoNumber,
  SelectInvoice,
  SelectPo,
} from 'src/sections/enter_invoice/GuideMeSteps';
import { IPO } from 'src/types/PO';
import PoGrid from '../enter_receipt/PoGrid';
import CreateInvoiceWithAttachment from './CreateInvoiceWithAttachment';
import CreatePo from './CreatePoWithReceipt';
import DocumentGrid from './DocumentGrid';

const EnterInvoiceMain = () => {
  const {
    selectedCompany: { companyId },
    currentStep,
    nextStep,
    searchPo,
    setSearchPo,
    supplierSearch,
    setSupplierSearch,
    lineItemSearch,
    setLineItemSearch,
    selectedInvoicePoRow,
    setSelectedInvoicePoRow,
    openCreatePoModal,
    toggleOpenCreatePoModal,
    openCreateInvoiceModal,
    toggleOpenCreateInvoiceModal,
    alert,
    setAlert,
    setSelectedInvoiceRow,
    invoiceNumber,
    setInvoiceNumber,
    setDisableStep,
    setGuideMeModalData,
    openGuideMe,
    isOcrRedirect,
    showPreview,
  } = useEnterInvoices();

  const { getAttachDocuments } = useAttachDocument();
  const invoicePoColumns = useInvoicePoColumns();

  useEffect(() => {
    if (companyId) {
      openGuideMe();
    }
    if (currentStep === 1) {
      setDisableStep((prev) => ({ ...prev, disablePrev: true }));
      setInvoiceNumber((prev) => ({ ...prev, disable: false }));
      setGuideMeModalData([EnterInvoice]);
    } else if (currentStep === 2) {
      setInvoiceNumber((prev) => ({ ...prev, disable: true }));
      setDisableStep((prev) => ({ ...prev, disablePrev: false }));
      setAlert(false);
      setGuideMeModalData([SelectInvoice]);
    } else if (currentStep === 3) {
      setSearchPo((prev) => ({ ...prev, disable: false }));
      setGuideMeModalData([EnterPoNumber]);
    } else if (currentStep === 4) {
      setSearchPo((prev) => ({ ...prev, disable: true }));
      setGuideMeModalData([SelectPo]);
    } else if (currentStep === 5) {
      setGuideMeModalData([ChooseInvoice]);
    }
  }, [
    currentStep,
    openGuideMe,
    companyId,
    setAlert,
    setDisableStep,
    setGuideMeModalData,
    setInvoiceNumber,
    setSearchPo,
  ]);

  useEffect(() => {
    if (companyId && !isOcrRedirect) getAttachDocuments();
  }, [getAttachDocuments, companyId, isOcrRedirect]);

  const handleInvoiceNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInvoiceNumber((prev) => ({ ...prev, value: e.target.value }));
    setAlert(false);
  };
  const handleSelectedSupplier = useCallback(
    (data: IPO) => {
      setSelectedInvoicePoRow(data);
    },
    [setSelectedInvoicePoRow]
  );

  const handleInvoiceKeyDown = useCallback(() => {
    if (!invoiceNumber.value) {
      setAlert(true);
    } else {
      setAlert(false);
      setInvoiceNumber((prev) => ({ ...prev, disable: true }));
      nextStep();
    }
  }, [invoiceNumber.value, nextStep, setAlert, setInvoiceNumber]);

  const handlePoSearch = useCallback(() => {
    setSearchPo((prev) => ({ ...prev, disable: true }));
    nextStep();
  }, [nextStep, setSearchPo]);

  const handlePoSelection = useCallback(() => {
    setDisableStep?.((prev) => ({ ...prev, disablePrev: true }));
    nextStep();
  }, [nextStep, setDisableStep]);

  return (
    <main>
      <div className="px-5 mt-2 grid grid-cols-4 gap-3">
        <div className="w-full">
          <SearchInput
            width="100%"
            title="Document #"
            value={invoiceNumber.value}
            onChange={handleInvoiceNumberChange}
            placeholder="Enter invoice or credit memo #"
            disabled={invoiceNumber.disable}
            onKeyDown={handleInvoiceKeyDown}
          />
          {alert && (
            <Alert
              message="Please enter a document number!"
              className="enter-invoice-alert alert-danger"
            />
          )}
        </div>
        {currentStep > 2 && (
          <SearchInput
            width="100%"
            title="PO #"
            value={searchPo.value}
            onChange={(e) => {
              setSearchPo(() => ({ value: e.target.value, disable: false }));
            }}
            placeholder="Enter PO # (Optional)"
            disabled={searchPo.disable}
            onKeyDown={handlePoSearch}
          />
        )}
        {currentStep === 4 && (
          <>
            <SearchInput
              width="100%"
              title="Filter PO's by Supplier Name"
              placeholder="Enter supplier name (Optional)"
              value={supplierSearch}
              onChange={(e) => {
                setSupplierSearch(e.target.value);
              }}
            />

            <SearchInput
              width="100%"
              title="Filter PO's by Item"
              value={lineItemSearch}
              placeholder="Enter item # or description (Optional)"
              onChange={(e) => {
                setLineItemSearch(e.target.value);
              }}
            />
          </>
        )}
      </div>
      {currentStep === 2 && (
        <DocumentGrid
          invoiceNumber={invoiceNumber.value}
          setSelectedInvoiceRow={setSelectedInvoiceRow}
          isDetailsPage={false}
        />
      )}
      {currentStep === 4 && (
        <>
          <PoGrid
            poNumber={searchPo.value}
            setSelectedInvoicePoRow={handleSelectedSupplier}
            companyId={companyId}
            supplierSearch={supplierSearch}
            setSupplierSearch={setSupplierSearch}
            lineItemSearch={lineItemSearch}
            setLineItemSearch={setLineItemSearch}
            columns={invoicePoColumns}
            setDisableStep={setDisableStep}
            isInvoice={true}
            isDetailsPage={false}
            isOcrRedirect={isOcrRedirect}
            handlePoSelection={handlePoSelection}
            gridKey={`${showPreview}`}
          />
          <CreatePo openModal={openCreatePoModal} closeModal={toggleOpenCreatePoModal} />
        </>
      )}
      {currentStep === 5 && (
        <>
          <DocumentGrid
            poId={selectedInvoicePoRow?.poId}
            poNumber={selectedInvoicePoRow?.poNum}
            setSelectedInvoiceRow={setSelectedInvoiceRow}
            isDetailsPage={false}
          />
          <CreateInvoiceWithAttachment
            openModal={openCreateInvoiceModal}
            closeModal={toggleOpenCreateInvoiceModal}
          />
        </>
      )}
    </main>
  );
};

export default EnterInvoiceMain;
